import {CheckIcon, XMarkIcon} from '@heroicons/react/24/outline'
import React, {useState} from 'react'
import {FormattedMessage} from 'react-intl'
import {useSelector} from 'react-redux'
import {useAssignSponsor} from '../../hooks/useAssignSponsor'
import {useNationalSponsorAssignments} from '../../hooks/useNationalSponsorAssignments'
import {Sponsor} from '../../hooks/useSponsors'
import {AppState} from '../../store/store'
import {SponsorImageKey} from '../../types/lessonKeys'
import {SponsorInfo} from '../../types/sponsorAssignments'
import Dialog from '../Dialog'
import FlatButton from '../FlatButton'
import ImageUpload from '../SponsorsPage/components/ImageUpload'
import NationalSponsorAssignmentTable from './NationalSponsorAssignmentTable'

type Props = Readonly<{
  sponsor: Sponsor | null
  sponsors: SponsorInfo[]
  open: boolean
  onClose: () => void
  onModulesSelected?: (modules: SponsorImageKey[]) => void
  onImageUpload?: (sponsor: Sponsor, image: File) => void
  onImageDelete?: (sponsor: Sponsor) => void
}>

function AssignNationalSponsorDialog({
  sponsor,
  sponsors,
  open,
  onClose,
  onModulesSelected,
  onImageUpload,
  onImageDelete
}: Props) {
  const state = useSelector((state: AppState) => state)
  const [saving, setSaving] = useState(false)

  const {
    assignments,
    isLoading: loading,
    fetchAssignments,
    clearAssignments,
    updateAssignments
  } = useNationalSponsorAssignments({
    onError: error => {
      console.error('Failed to load national sponsor assignments:', error)
    }
  })

  // Load existing assignments when dialog opens
  React.useEffect(() => {
    if (open && sponsor) {
      fetchAssignments(state)
    }
  }, [open, sponsor, fetchAssignments, state])

  // Reset state when dialog closes
  React.useEffect(() => {
    if (!open) {
      clearAssignments()
    }
  }, [open, clearAssignments])

  const handleModuleChange = (
    moduleKey: SponsorImageKey,
    isAssigned: boolean
  ) => {
    const newAssignments = assignments.map(assignment =>
      assignment.moduleKey === moduleKey
        ? {...assignment, sponsorId: isAssigned ? sponsor?.id || null : null}
        : assignment
    )
    updateAssignments(newAssignments)
  }

  const handleSelectAll = () => {
    if (!sponsor) return
    const newAssignments = assignments.map(assignment => {
      // Only assign if not already assigned to a different sponsor
      const isAvailable =
        !assignment.sponsorId || assignment.sponsorId === sponsor.id

      return {
        ...assignment,
        sponsorId: isAvailable ? sponsor.id : assignment.sponsorId
      }
    })
    updateAssignments(newAssignments)
  }

  const handleSelectNone = () => {
    if (!sponsor) return
    const newAssignments = assignments.map(assignment => ({
      ...assignment,
      sponsorId:
        assignment.sponsorId === sponsor.id ? null : assignment.sponsorId
    }))
    updateAssignments(newAssignments)
  }

  const {assignSponsor} = useAssignSponsor({
    onSuccess: () => {
      if (!sponsor) return

      const selectedModules = assignments
        .filter(assignment => assignment.sponsorId === sponsor.id)
        .map(assignment => assignment.moduleKey)

      // Call the callback if provided
      if (onModulesSelected) {
        onModulesSelected(selectedModules)
      }

      onClose()
      setSaving(false)
    },
    onError: error => {
      console.error(
        'Failed to save national sponsor module assignments:',
        error
      )
      setSaving(false)
    }
  })

  const handleSave = () => {
    if (!sponsor) return

    setSaving(true)
    // Save the current assignments using the new API
    assignSponsor({type: 'national', assignments, state})
  }

  const handleCancel = () => {
    onClose()
  }

  const handleImageUpload = (_: string, image: File) => {
    if (sponsor && onImageUpload) {
      onImageUpload(sponsor, image)
    }
  }

  const handleImageDelete = () => {
    if (sponsor && onImageDelete) {
      onImageDelete(sponsor)
    }
  }

  return (
    <Dialog open={open} onClose={handleCancel}>
      <div className="mx-auto flex w-full max-w-2xl flex-col">
        <div className="-mt-6 mb-6 flex w-full items-center justify-between">
          <div className="items-e flex gap-4">
            {sponsor && (
              <ImageUpload
                uploadKey={sponsor.id}
                uri={sponsor.imageUrl || null}
                onUpload={handleImageUpload}
                onDelete={handleImageDelete}
                size="small"
              />
            )}
            <Dialog.Title className="typography-headline-4">
              <FormattedMessage id="admin.sponsors.assignNational.title" />
            </Dialog.Title>
          </div>
          <button
            className="-mr-2 -mt-2"
            type="button"
            aria-label="Close"
            onClick={handleCancel}
          >
            <span className="sr-only">
              <FormattedMessage id="general.Close" />
            </span>
            <XMarkIcon aria-hidden className="size-7" />
          </button>
        </div>

        <div className="mb-4">
          <p className="typography-body-2 mb-4 text-grey-tertiary-30">
            <FormattedMessage id="admin.sponsors.assignNational.selectModules" />
          </p>

          {/* Bulk selection buttons */}
          <div className="mb-4 flex w-full items-end justify-end gap-2">
            <FlatButton
              variant="success"
              onClick={handleSelectAll}
              icon={
                <XMarkIcon className="size-4 rounded-sm border border-black" />
              }
            >
              <FormattedMessage id="admin.sponsors.assignNational.assignToAll" />
            </FlatButton>
            <FlatButton
              variant="secondary"
              onClick={handleSelectNone}
              icon={
                <CheckIcon className="size-4 rounded-sm border border-black" />
              }
            >
              <FormattedMessage id="admin.sponsors.assignNational.unassignFromAll" />
            </FlatButton>
          </div>
        </div>

        {/* Module Assignment Table */}
        {sponsor && assignments.length > 0 && (
          <NationalSponsorAssignmentTable
            assignments={assignments}
            currentSponsor={{id: sponsor.id, name: sponsor.name}}
            allSponsors={sponsors}
            isLoading={loading}
            onChange={handleModuleChange}
          />
        )}

        {/* Save/Cancel Buttons */}
        <div className="mt-4 flex justify-end space-x-3">
          <FlatButton
            variant="secondary"
            onClick={handleCancel}
            disabled={saving}
          >
            <FormattedMessage id="admin.manageSponsors.cancel" />
          </FlatButton>
          <FlatButton variant="success" onClick={handleSave} disabled={saving}>
            Save
          </FlatButton>
        </div>
      </div>
    </Dialog>
  )
}

export default AssignNationalSponsorDialog
